import type {
  ShortCircuitCurrentResults,
  DomainGuid,
  ShortCircuitElementResults,
  ShortCircuitScenarioResults,
  ShortCircuitScenarioElementCalculation
} from '../../..';
import { Amperes } from '../../../domain/ElectricCurrent';
import type { FunctionalGridElementTypes } from '../../../grid/FunctionalGrid';

export class ShortCircuitProperty {
  private static readonly currentPhase1Min =
    nameOf<ShortCircuitCurrentResults>('currentPhase1Min');

  private static readonly currentPhase1Max =
    nameOf<ShortCircuitCurrentResults>('currentPhase1Max');

  private static readonly currentPhase2Min =
    nameOf<ShortCircuitCurrentResults>('currentPhase2Min');

  private static readonly currentPhase3Max =
    nameOf<ShortCircuitCurrentResults>('currentPhase3Max');

  constructor(
    readonly gridElementId: DomainGuid,
    readonly faultCurrent: Amperes,
    readonly name: ShortCircuitCurrentResultKeys
  ) {}

  static from1PhaseToGroundMin: CreateShortCircuitProperty = (
    gridElementId: DomainGuid,
    results: ShortCircuitElementResults
  ) => {
    const name = this.currentPhase1Min;
    return new ShortCircuitProperty(
      gridElementId,
      Amperes.fromAmperes(results[name]),
      name
    );
  };

  static from1PhaseToGroundMax: CreateShortCircuitProperty = (
    gridElementId: DomainGuid,
    results: ShortCircuitElementResults
  ) => {
    const name = this.currentPhase1Max;
    return new ShortCircuitProperty(
      gridElementId,
      Amperes.fromAmperes(results[name]),
      name
    );
  };

  static from2PhasesMin: CreateShortCircuitProperty = (
    gridElementId: DomainGuid,
    results: ShortCircuitElementResults
  ) => {
    const name = this.currentPhase2Min;
    return new ShortCircuitProperty(
      gridElementId,
      Amperes.fromAmperes(results[name]),
      name
    );
  };

  static from3PhasesMax: CreateShortCircuitProperty = (
    gridElementId: DomainGuid,
    results: ShortCircuitElementResults
  ) => {
    const name = this.currentPhase3Max;
    return new ShortCircuitProperty(
      gridElementId,
      Amperes.fromAmperes(results[name]),
      name
    );
  };
}

export function nameOf<T>(key: keyof T) {
  return key;
}

export type ShortCircuitCurrentResultKeys = keyof ShortCircuitCurrentResults;

export type CreateShortCircuitProperty = (
  gridElementId: DomainGuid,
  results: ShortCircuitElementResults
) => ShortCircuitProperty;

export const createFaultProperties = (
  scenarios: ShortCircuitScenarioResults[],
  objectType: FunctionalGridElementTypes,
  createProperty: CreateShortCircuitProperty
): ShortCircuitProperty[] => {
  return scenarios
    .flatMap(scenario => scenario.results)
    .filter(gridElement => gridElement.objectType === objectType)
    .map(scenarioResults =>
      createProperty(scenarioResults.id, scenarioResults.result)
    );
};

export class FaultsProperties {
  private constructor(
    private readonly results: ShortCircuitScenarioElementCalculation[]
  ) {}

  static fromManyScenarios(scenarios: ShortCircuitScenarioResults[]) {
    const results = scenarios.flatMap(scenario => scenario.results);
    return {
      for: (objectType: FunctionalGridElementTypes) =>
        new FaultsProperties(
          results.filter(gridElement => gridElement.objectType === objectType)
        )
    };
  }

  static fromResults(results: ShortCircuitScenarioElementCalculation[]) {
    return {
      for: (objectType: FunctionalGridElementTypes) =>
        new FaultsProperties(
          results.filter(gridElement => gridElement.objectType === objectType)
        )
    };
  }

  from1PhaseToGroundMin = (): ShortCircuitProperty[] =>
    this.results.map(result =>
      ShortCircuitProperty.from1PhaseToGroundMin(result.id, result.result)
    );

  from1PhaseToGroundMax = (): ShortCircuitProperty[] =>
    this.results.map(result =>
      ShortCircuitProperty.from1PhaseToGroundMax(result.id, result.result)
    );

  from2PhasesMin = (): ShortCircuitProperty[] =>
    this.results.map(result =>
      ShortCircuitProperty.from2PhasesMin(result.id, result.result)
    );

  from3PhasesMax = (): ShortCircuitProperty[] =>
    this.results.map(result =>
      ShortCircuitProperty.from3PhasesMax(result.id, result.result)
    );
}
